import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useTheme, THEMES } from '../context/ThemeContext';
import { clearPermissions } from '../utils/permissions';

/**
 * ClientHeader Component
 * 
 * Layout mirrors recruiter Header: Logo → Nav → spacer → Search → Theme → Avatar
 */

const ClientHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === THEMES.dark;

  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('candidates');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const searchRef = useRef(null);
  const resultsRef = useRef(null);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const initials = (user.name || 'U').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('userId');
    clearPermissions();
    window.location.href = '/client/login';
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target) && resultsRef.current && !resultsRef.current.contains(e.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!searchQuery.trim()) { setSearchResults([]); setShowResults(false); return; }
    const timer = setTimeout(() => performSearch(), 300);
    return () => clearTimeout(timer);
  }, [searchQuery, searchType]);

  const performSearch = async () => {
    if (!searchQuery.trim()) return;
    setIsSearching(true);
    try {
      const token = localStorage.getItem('authToken');
      if (searchType === 'candidates') {
        const response = await axios.get(`/api/client/archive?search=${encodeURIComponent(searchQuery)}`, { headers: { Authorization: `Bearer ${token}` } });
        setSearchResults(response.data.slice(0, 8));
      } else {
        const response = await axios.get('/api/client/pipelines', { headers: { Authorization: `Bearer ${token}` } });
        const filtered = response.data.filter(p => p.name.toLowerCase().includes(searchQuery.toLowerCase()) || p.description?.toLowerCase().includes(searchQuery.toLowerCase())).slice(0, 8);
        setSearchResults(filtered);
      }
      setShowResults(true);
    } catch (error) { console.error('Search error:', error); }
    finally { setIsSearching(false); }
  };

  const handleResultClick = (result) => {
    if (searchType === 'candidates') navigate(`/client/candidate/${result.candidate_id}`);
    else navigate(`/client/pipeline/${result.id}`);
    setShowResults(false);
    setSearchQuery('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') { setShowResults(false); setSearchQuery(''); }
    else if (e.key === 'Enter' && searchQuery.trim()) {
      if (searchType === 'candidates') navigate(`/client/archive?search=${encodeURIComponent(searchQuery)}`);
      setShowResults(false);
    }
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');
  const navLinkClass = (path) =>
    `px-2.5 py-1 text-xs font-medium rounded transition-colors ${
      isActive(path)
        ? 'text-emerald-700 bg-emerald-50 border border-emerald-200'
        : 'text-gray-500 hover:text-emerald-700 hover:bg-emerald-50'
    }`;

  return (
    <header className="bg-white border-b sticky top-0 z-40 h-[44px]">
      <div className="max-w-full px-4 h-full flex items-center">
        
        {/* Left: Logo + Nav */}
        <div className="flex items-center gap-4 flex-shrink-0">
          <img src="/Logo-Symbol-Colored.svg" alt="Logo" className="w-6 h-6 flex-shrink-0 cursor-pointer" onClick={() => navigate('/client/dashboard')} />
          <nav className="flex items-center gap-0.5">
            <button onClick={() => navigate('/client/dashboard')} className={navLinkClass('/client/dashboard')}>Dashboard</button>
            <button onClick={() => navigate('/client/archive')} className={navLinkClass('/client/archive')}>Candidates</button>
            <button onClick={() => navigate('/client/tags')} className={navLinkClass('/client/tags')}>Tags</button>
          </nav>
        </div>

        {/* Spacer */}
        <div className="flex-1 min-w-[40px]" />

        {/* Right cluster */}
        <div className="flex items-center gap-2">
          
          {/* Search */}
          <div className="relative w-64" ref={searchRef}>
            <div className="flex items-center bg-gray-50 border border-gray-200 rounded-lg overflow-hidden focus-within:ring-1 focus-within:ring-blue-500 focus-within:border-blue-500">
              <select value={searchType} onChange={(e) => { setSearchType(e.target.value); setSearchResults([]); }} className="bg-transparent text-[11px] text-gray-500 pl-2 pr-0.5 py-1 border-r border-gray-200 focus:outline-none cursor-pointer">
                <option value="candidates">Candidates</option>
                <option value="searches">Searches</option>
              </select>
              <div className="flex-1 flex items-center">
                <svg className="w-3.5 h-3.5 text-gray-400 ml-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} onFocus={() => searchQuery.trim() && setShowResults(true)} onKeyDown={handleKeyDown} placeholder="Search..." className="flex-1 bg-transparent text-xs px-1.5 py-1.5 focus:outline-none min-w-0" />
                {isSearching && <div className="mr-1.5"><div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-gray-300 border-t-blue-500"></div></div>}
                {searchQuery && !isSearching && (
                  <button onClick={() => { setSearchQuery(''); setSearchResults([]); setShowResults(false); }} className="mr-1.5 text-gray-400 hover:text-gray-600">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                )}
              </div>
            </div>

            {/* Search Results Dropdown */}
            {showResults && searchQuery.trim() && searchResults.length > 0 && (
              <div ref={resultsRef} className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-[400px] overflow-auto">
                {searchType === 'candidates' ? (
                  <>
                    <div className="px-3 py-1.5 text-xs font-medium text-gray-500 border-b bg-gray-50">Candidates ({searchResults.length})</div>
                    {searchResults.map((c) => (
                      <button key={c.candidate_id} onClick={() => handleResultClick(c)} className="w-full px-3 py-2 text-left hover:bg-blue-50 flex items-center gap-3 border-b border-gray-100 last:border-0">
                        {c.profile_picture_url ? <img src={c.profile_picture_url} alt="" className="w-7 h-7 rounded-full object-cover" /> : <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 text-xs font-medium">{c.name?.charAt(0)?.toUpperCase()}</div>}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">{c.name}</div>
                          <div className="text-xs text-gray-500 truncate">{c.client_current_title || c.current_title || 'No title'}{(c.client_current_company || c.current_company) && ` at ${c.client_current_company || c.current_company}`}</div>
                        </div>
                      </button>
                    ))}
                    <button onClick={() => { navigate(`/client/archive?search=${encodeURIComponent(searchQuery)}`); setShowResults(false); setSearchQuery(''); }} className="w-full px-3 py-2 text-xs text-blue-600 hover:bg-blue-50 text-center font-medium">View all in Archive →</button>
                  </>
                ) : (
                  <>
                    <div className="px-3 py-1.5 text-xs font-medium text-gray-500 border-b bg-gray-50">Searches ({searchResults.length})</div>
                    {searchResults.map((p) => (
                      <button key={p.id} onClick={() => handleResultClick(p)} className="w-full px-3 py-2 text-left hover:bg-blue-50 flex items-center gap-3 border-b border-gray-100 last:border-0">
                        <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-green-100 text-green-600">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">{p.name}</div>
                          <div className="text-xs text-gray-500">{p.status === 'active' ? 'Active' : 'Archived'}{p.candidate_count !== undefined && ` • ${p.candidate_count} candidates`}</div>
                        </div>
                      </button>
                    ))}
                  </>
                )}
              </div>
            )}
            {showResults && searchQuery.trim() && searchResults.length === 0 && !isSearching && (
              <div ref={resultsRef} className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-4 z-50">
                <p className="text-sm text-gray-500 text-center">No {searchType === 'candidates' ? 'candidates' : 'searches'} found</p>
              </div>
            )}
          </div>

          {/* Theme Toggle */}
          <button onClick={toggleTheme} className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors" title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}>
            {isDark ? (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" /></svg>
            )}
          </button>

          {/* Profile Avatar */}
          <div className="relative">
            <button onClick={() => setProfileOpen(!profileOpen)} className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-[11px] font-semibold hover:ring-2 hover:ring-blue-300 transition-all flex-shrink-0" title={user.name || 'Profile'}>
              {initials}
            </button>
            {profileOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setProfileOpen(false)} />
                <div className="absolute right-0 mt-1.5 w-52 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1">
                  <div className="px-3 py-2 border-b">
                    <p className="text-sm font-medium text-gray-900 truncate">{user.name || 'User'}</p>
                    <p className="text-xs text-gray-500 truncate">{user.email || ''}</p>
                  </div>
                  <button onClick={() => { navigate('/client/profile'); setProfileOpen(false); }} className="w-full px-3 py-2 text-sm text-left hover:bg-gray-50 flex items-center gap-2 text-gray-700">
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                    My Profile
                  </button>
                  <button onClick={() => { navigate('/client/user-management'); setProfileOpen(false); }} className="w-full px-3 py-2 text-sm text-left hover:bg-gray-50 flex items-center gap-2 text-gray-700">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
                      User Management
                  </button>
                  <div className="border-t my-1"></div>
                  <button onClick={() => { handleLogout(); setProfileOpen(false); }} className="w-full px-3 py-2 text-sm text-left text-red-600 hover:bg-red-50 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
                    Sign Out
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default ClientHeader;
